import { useFlags } from "launchdarkly-react-client-sdk";
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";

import { useCurrentUser } from "./use-current-user";

const onboardingContext = createContext();

export function useOnboardingContext() {
  return useContext(onboardingContext);
}

function useProvideOnboarding() {
  const [shouldShowFirstLoginTour, setShouldShowFirstLoginTour] =
    useState(null);

  return {
    shouldShowFirstLoginTour,
    setShouldShowFirstLoginTour,
  };
}

export function OnboardingProvider({ children }) {
  const onboarding = useProvideOnboarding();

  return (
    <onboardingContext.Provider value={onboarding}>
      {children}
    </onboardingContext.Provider>
  );
}

/**
 * Provides onboarding/milestone state.
 *
 * When `releaseWebEnableFtueTour` is ON, this hook does NOT redirect — the
 * /welcome page is triggered exclusively by the registration flow via
 * useRedirectAuthenticatedUsers. `isOnboardingComplete` tracks whether the
 * user has finished all onboarding milestones, NOT whether they've seen the
 * welcome page.
 *
 * When the flag is OFF, the legacy redirect to /onboarding is preserved.
 */
export function useOnboarding() {
  const { data, loading, refetch } = useCurrentUser();
  const { releaseWebEnableFtueTour } = useFlags();

  const navigate = useNavigate();
  const location = useLocation();

  const { shouldShowFirstLoginTour, setShouldShowFirstLoginTour } =
    useOnboardingContext();

  const user = data?.user;

  // Legacy redirect: only when the new onboarding flag is OFF
  useEffect(() => {
    if (releaseWebEnableFtueTour) return;

    if (!loading && user) {
      const onboardingSkipped = localStorage.getItem(
        "onboardingSkipped",
      );

      const isOnOnboardingPage =
        window.location.pathname.startsWith("/onboarding");
      const justCompletedOnboarding =
        window.location.pathname === "/dashboard" &&
        location?.state?.fromOnboarding;
      const isInPimsPalIntegration =
        window.location.pathname.startsWith("/pimspal");

      if (
        !user?.isOnboardingComplete &&
        !onboardingSkipped &&
        !isOnOnboardingPage &&
        !justCompletedOnboarding &&
        !isInPimsPalIntegration
      ) {
        navigate("/onboarding");
      } else {
        setShouldShowFirstLoginTour(false);
      }
    }
  }, [
    user,
    loading,
    navigate,
    location?.state?.fromOnboarding,
    setShouldShowFirstLoginTour,
    releaseWebEnableFtueTour,
  ]);

  const skipOnboarding = () => {
    localStorage.setItem("onboardingSkipped", "true");
  };

  return {
    isOnboardingComplete: user?.isOnboardingComplete,
    refetchUserOnboardingInfo: refetch,
    shouldShowFirstLoginTour,
    setShouldShowFirstLoginTour,
    skipOnboarding,
    loading,
  };
}
